/**
 * components/hire/editProfileModal.tsx
 * Correct a bad extraction by editing the fields — no document, no AI spend.
 *
 * "Update resume" needs a new document; this needs none. When the extractor got
 * something wrong (4 years read as 14, a must-have skill missed), the recruiter
 * fixes the field directly.
 *
 * It saves as the NEXT resume version rather than overwriting the current one:
 * profile rows are immutable per version at the database level
 * (fn_hire_profiles_guard_update, migration 0010), so a score can always be
 * explained by a version nobody has silently rewritten. v1 keeps what the
 * extractor read; v2 records what a human corrected it to.
 *
 * Every field here feeds matching, so the server re-derives the lexical text,
 * skill_id rows and the embedding. A correction therefore marks any stored match
 * ranking containing this candidate as out of date, and the job screen offers to
 * re-score just this person.
 */
import React, { useEffect, useState } from 'react';
import { View, Text, TextInput } from 'react-native';
import { useTheme } from '../../context/themeContext';
import { Modal, ActionButton, Icon } from '../ui';
import { web } from '../../utils/web';
import { showToast } from '../toastComponent';
import { getApiErrorMessage } from '../../utils/errorHandler';
import { updateCandidateProfile } from '../../services/hire/candidateV2';
import type { CandidateDetail } from '../../schemas/hire/candidateV2';

/** "iv cannulation, ct scan operation" → [{slug, years}] */
function parseSkills(text: string, previous: { slug: string; years: number }[]): { slug: string; years: number }[] {
  const yearsBySlug = new Map(previous.map((s) => [s.slug, s.years]));
  return text
    .split(',')
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean)
    .filter((s, i, arr) => arr.indexOf(s) === i)
    // Keep the extracted years for a skill that survives the edit; a newly typed
    // skill has no years on the resume to read, so it starts at 0.
    .map((slug) => ({ slug, years: yearsBySlug.get(slug) ?? 0 }));
}

export default function EditProfileModal({
  open, candidate, onClose, onUpdated,
}: {
  open: boolean;
  candidate: CandidateDetail | null;
  onClose: () => void;
  onUpdated: (c: CandidateDetail) => void;
}) {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight, radii } = theme;

  const [skills, setSkills] = useState('');
  const [exp, setExp] = useState('');
  const [location, setLocation] = useState('');
  const [notice, setNotice] = useState('');
  const [salary, setSalary] = useState('');
  const [summary, setSummary] = useState('');
  const [busy, setBusy] = useState(false);

  const p = candidate?.profile ?? null;

  useEffect(() => {
    if (!open) return;
    setSkills((p?.skills ?? []).map((s) => s.slug).join(', '));
    setExp(p?.totalExperienceYears != null ? String(p.totalExperienceYears) : '');
    setLocation(p?.currentLocation ?? '');
    setNotice(p?.noticePeriodDays != null ? String(p.noticePeriodDays) : '');
    setSalary(p?.expectedSalary != null ? String(p.expectedSalary) : '');
    setSummary(p?.summaryCompressed ?? '');
    setBusy(false);
  }, [open, candidate?.id]);

  const close = () => { if (!busy) onClose(); };

  const onSave = async () => {
    if (!candidate) return;
    const expNum = exp.trim() === '' ? null : Number(exp);
    const noticeNum = notice.trim() === '' ? null : Number(notice);
    const salaryNum = salary.trim() === '' ? null : Number(salary);
    if (expNum != null && (!Number.isFinite(expNum) || expNum < 0 || expNum > 60)) {
      showToast('error', 'Check the experience', 'Total experience must be between 0 and 60 years.');
      return;
    }
    if (noticeNum != null && (!Number.isInteger(noticeNum) || noticeNum < 0 || noticeNum > 365)) {
      showToast('error', 'Check the notice period', 'Notice period must be a whole number of days (0–365).');
      return;
    }
    if (salaryNum != null && (!Number.isFinite(salaryNum) || salaryNum < 0)) {
      showToast('error', 'Check the expected salary', 'Expected salary cannot be negative.');
      return;
    }
    setBusy(true);
    try {
      const updated = await updateCandidateProfile(candidate.id, {
        skills: parseSkills(skills, p?.skills ?? []),
        totalExperienceYears: expNum,
        currentLocation: location.trim() || null,
        noticePeriodDays: noticeNum,
        expectedSalary: salaryNum,
        summaryCompressed: summary.trim() || null,
      });
      showToast(
        'success',
        `Corrected — now v${updated.profile?.resumeVersion ?? ''}`,
        `${updated.name}'s profile was corrected; matching now uses these values.`,
      );
      onUpdated(updated);
    } catch (e) {
      showToast('error', 'Could not update the profile', getApiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const label = (t: string) => (
    <Text style={{ fontSize: fontSize.xs, color: colors.ink3, fontWeight: fontWeight.semibold, textTransform: 'uppercase', letterSpacing: 0.6 }}>{t}</Text>
  );
  const inputStyle = [
    {
      paddingHorizontal: 12, paddingVertical: 10, borderWidth: 1, borderColor: colors.line,
      borderRadius: radii.md, backgroundColor: colors.paper2, color: colors.ink, fontSize: fontSize.md,
    },
    web({ outline: 'none' }),
  ];

  return (
    <Modal
      open={open}
      onClose={close}
      title={candidate ? `Edit profile — ${candidate.name}` : 'Edit profile'}
      size="wide"
      footer={
        <View style={{ flexDirection: 'row', gap: 8 }}>
          <ActionButton label="Cancel" variant="secondary" onPress={close} />
          <ActionButton label="Save changes" variant="primary" icon="check" onPress={onSave} loading={busy} />
        </View>
      }
    >
      <View style={{ gap: 16 }}>
        <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 9, paddingVertical: 10, paddingHorizontal: 12, borderRadius: radii.md, borderWidth: 1, borderColor: colors.line, backgroundColor: colors.paper2 }}>
          <Icon name="info" size={14} color={colors.ink3} />
          <Text style={{ flex: 1, fontSize: fontSize.xs, color: colors.ink3, lineHeight: 17 }}>
            Saves as resume v{(p?.resumeVersion ?? 1) + 1} — a corrected copy of v{p?.resumeVersion ?? 1}, no new document and
            no AI spend. Profile versions are immutable by design, so a correction is recorded rather than overwritten: v{p?.resumeVersion ?? 1} keeps
            what the extractor read. These fields drive matching, so jobs already matched against this candidate will offer to re-score them.
          </Text>
        </View>

        <View style={{ gap: 6 }}>
          {label('Skills · comma separated')}
          <TextInput
            value={skills}
            onChangeText={setSkills}
            placeholder="e.g. x-ray positioning, ct scan operation, radiation safety"
            placeholderTextColor={colors.ink4}
            multiline
            style={[...inputStyle, { minHeight: 74, textAlignVertical: 'top' }]}
          />
          <Text style={{ fontSize: fontSize.xs, color: colors.ink4 }}>
            Must-have matching runs on these. A skill kept from the extraction keeps its years; a newly typed one starts at 0.
          </Text>
        </View>

        <View style={{ flexDirection: 'row', gap: 12 }}>
          <View style={{ flex: 1, gap: 6 }}>
            {label('Total experience (years)')}
            <TextInput value={exp} onChangeText={(v) => setExp(v.replace(/[^\d.]/g, ''))} placeholder="e.g. 5" placeholderTextColor={colors.ink4} style={inputStyle} />
          </View>
          <View style={{ flex: 1, gap: 6 }}>
            {label('Current location')}
            <TextInput value={location} onChangeText={setLocation} placeholder="e.g. Coimbatore" placeholderTextColor={colors.ink4} style={inputStyle} />
          </View>
        </View>

        <View style={{ flexDirection: 'row', gap: 12 }}>
          <View style={{ flex: 1, gap: 6 }}>
            {label('Notice period (days)')}
            <TextInput value={notice} onChangeText={(v) => setNotice(v.replace(/[^\d]/g, ''))} placeholder="e.g. 30" placeholderTextColor={colors.ink4} style={inputStyle} />
          </View>
          <View style={{ flex: 1, gap: 6 }}>
            {label('Expected salary')}
            <TextInput value={salary} onChangeText={(v) => setSalary(v.replace(/[^\d.]/g, ''))} placeholder="e.g. 480000" placeholderTextColor={colors.ink4} style={inputStyle} />
          </View>
        </View>

        <View style={{ gap: 6 }}>
          {label('Summary')}
          <TextInput
            value={summary}
            onChangeText={setSummary}
            placeholder="The short profile summary shown on the candidate page."
            placeholderTextColor={colors.ink4}
            multiline
            style={[...inputStyle, { minHeight: 110, textAlignVertical: 'top' }]}
          />
        </View>
      </View>
    </Modal>
  );
}
